/**
 * Contiguity validation for grid entities (plots, beds)
 * Uses BFS to ensure all cells are edge-connected (4-directional connectivity)
 */

interface Cell {
  row: number;
  col: number;
}

/**
 * Check if cells form a contiguous shape (all cells edge-connected)
 * Handles coordinate 0 skip: -1 and 1 are adjacent (4-directional connectivity)
 * @param cells Array of {row, col} cells
 * @returns true if contiguous, false if gaps or diagonal-only connections
 */
export const isContiguous = (cells: Cell[]): boolean => {
  if (cells.length === 0) return true;
  if (cells.length === 1) return true;

  // Build adjacency map for O(1) lookup
  const cellSet = new Set<string>();
  cells.forEach(c => cellSet.add(`${c.row}:${c.col}`));

  // BFS from first cell
  const visited = new Set<string>();
  const queue: Cell[] = [cells[0]];
  visited.add(`${cells[0].row}:${cells[0].col}`);

  // Helper to get next coordinate (skip 0)
  const nextCoord = (coord: number, delta: number): number => {
    const next = coord + delta;
    // Handle coordinate 0 skip
    if (coord === -1 && delta === 1) return 1;
    if (coord === 1 && delta === -1) return -1;
    return next;
  };

  const directions = [
    { dr: -1, dc: 0 }, // top
    { dr: 1, dc: 0 },  // bottom
    { dr: 0, dc: -1 }, // left
    { dr: 0, dc: 1 },  // right
  ];

  while (queue.length > 0) {
    const current = queue.shift()!;
    
    // Check 4-directional neighbors (accounting for coordinate 0 skip)
    for (const { dr, dc } of directions) {
      const neighborRow = dr !== 0 ? nextCoord(current.row, dr) : current.row;
      const neighborCol = dc !== 0 ? nextCoord(current.col, dc) : current.col;
      const neighborKey = `${neighborRow}:${neighborCol}`;
      
      if (cellSet.has(neighborKey) && !visited.has(neighborKey)) {
        visited.add(neighborKey);
        queue.push({ row: neighborRow, col: neighborCol });
      }
    }
  }

  // All cells must be reachable
  return visited.size === cells.length;
};

/**
 * Validate contiguity and return error message if invalid
 */
export const validateContiguity = (cells: Cell[], entityName: string = 'Entity'): string | null => {
  if (cells.length === 0) {
    return `${entityName} must have at least one cell`;
  }
  
  if (!isContiguous(cells)) {
    return `${entityName} cells must form a contiguous shape (no gaps, diagonal-only connections not allowed)`;
  }
  
  return null;
};
